// SC-2376 table-query contract (request/response shapes for POST /catalogs/{id}/table).
// Kept in a sibling module to contain this large file; re-exported so callers
// still `import { TableQuery, ... } from '../types'`. Type-only, so the
// table.ts → index.ts back-import (FindingStatus) erases at compile — no cycle.
export * from './table';
// SC-2376 Slice E facets (POST /catalogs/{id}/table/facets) — the facet rail's
// counts (SC-2395). Sibling module like ./table; re-exported so callers still
// `import { FacetGroup, ... } from '../types'`.
export * from './facets';
// Canvas P0 (SC-2653) — the CanvasDirective contract that generalizes the table's
// ViewDirective to the whole workspace. Sibling module like ./table; re-exported so
// callers still `import { CanvasDirective, isCanvasDirective } from '../types'`.
export * from './canvas';
// Generative boards (SC-2872) — the ViewSpec grammar v1 (FE mirror of SC-2869)
// plus the Board/bind wire contract (SC-2870). Sibling module like ./table.
export * from './boards';
import type { ColumnFlag, Highlight, RowFlag, TableFilter } from './table';

// Vertical: BE is source of truth (per SC-1841 Q6). Treat as opaque string;
// FE doesn't narrow on values. Render `vertical_label` (humanized) wherever
// possible. The override picker (future ticket) will fetch the canonical list
// from SC-1842's `GET /verticals` endpoint.
export type Vertical = string;

export type CatalogStatus = 'pending' | 'active' | 'partial' | 'archived';

export interface User {
  id: string;
  email: string;
  name: string;
  created_at: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
}

// Literal union — BE drift surfaces at compile time. If you add a
// Record<Role, …> lookup, provide a fallback for unknown roles since the
// BE source-of-truth list could grow.
export type Role = 'ekom_admin' | 'org_owner' | 'org_admin' | 'org_user';

// Capability strings mirroring the BE `Permission` enum (snake_case wire
// values). RBAC gates on these, not on role strings (SC-2620). Only the
// permissions the FE actually gates on need to be listed; grow as surfaces
// arrive. See src/auth/permissions.ts for the membership-level check.
export type Permission =
  | 'invite_member'
  | 'manage_destination_membership'
  | 'manage_destinations'
  | 'view_sources'
  | 'edit_entity_value'
  | 'edit_member_title'
  | 'apply_finding';

export type MembershipStatus = 'active' | 'inactive' | 'pending';

export interface Membership {
  id: string;
  role: Role;
  status: MembershipStatus;
  organization: Organization | null;
  // The caller's effective permissions in this org, sourced from the BE
  // (role → permissions resolved server-side). OPTIONAL until the BE surfaces
  // it on `/me` + auth responses (the SSO-invite BE slice); `membershipHasPermission`
  // falls back to a role-derived check until then, so gating call sites don't
  // change when it lands.
  permissions?: Permission[];
}

export interface CatalogResponse {
  id: string;
  name: string;
  field_names: string[];
  entity_count: number;
  vertical: Vertical;
  vertical_label: string;
  summary: string | null;
  // LLM-determined row-key field(s). Empty until the profile pass completes.
  // Replaces the legacy "first locked field" idiom for identity reads (SC-2233).
  identifier_fields: string[];
  // The field(s) a person calls an entity by (e.g. `stock_number` where the key
  // is `vin`) — a salience pick, NOT unique. The export pins these ahead of
  // `identifier_fields` in its identity band (SC-3232). `[]` until profiled.
  display_order: string[];
  // Reserved for future "don't auto-modify" lock semantics. Currently always
  // empty unless a user explicitly sets it; no FE consumer (SC-2233).
  locked_fields: string[];
  status: CatalogStatus;
  source: string;
  created_at: string;
  updated_at: string;
  // Per-catalog entity terminology (SC-2191). BE supplies "product"/"products"
  // defaults for legacy/unprofiled catalogs; the LLM-driven CatalogProfile
  // overrides per catalog (e.g. "athlete"/"athletes").
  entity_term_singular: string;
  entity_term_plural: string;
}

export interface EntityResponse {
  id: string;
  identifier: string | null;
  index: number;
  data: Record<string, unknown>;
  // SC-2376 Slice H — single-entity findings overlay bundled into the detail
  // read, at FULL-field scope (the `/table` overlay is projection-scoped; the
  // drawer shows every attribute, so it must surface findings on hidden fields).
  // Same shapes the table uses, with ONE delta: `row_flag` is SINGULAR (one
  // entity — the drawer header badge), not the `row_flags[]` list.
  //
  // OPTIONAL: present once Slice H (#75) lands on the API; the pre-Slice-H
  // response omits them entirely, so read sites must default empty/null (the
  // drawer renders a bare doc until then). All the `/table` overlay caveats
  // apply (flag/suggestion split, OPEN-only painting, `severity` is the NAME,
  // a `Highlight` carries both `severity` and `confidence`).
  highlights?: Highlight[];
  row_flag?: RowFlag | null;
  column_flags?: ColumnFlag[];
  // SC-3104 — the channels this entity is a MEMBER of, by destination id. The
  // drawer is the only place this is visible: `/table` rows deliberately don't
  // carry membership (it's a per-row lookup across 120k rows), so there can be
  // no Channels column — the table's channel view is the `destination` filter.
  //
  // NOT the whole truth about where the row goes: an `all_entities` destination
  // receives every row without tagging any of them, so this list can be empty
  // for an entity that reaches three channels. Read it as "explicit membership",
  // and let the `destination` filter answer "reaches".
  //
  // OPTIONAL until the SC-3104 backend deploys; read sites must default to [].
  destination_ids?: string[];
  // SC-3659 / SC-3660 — fields that changed in EKOM but aren't delivered to one
  // of this entity's destinations. SPARSE: only out-of-sync (field, destination)
  // pairs, `[]` when everything is delivered. The backend already leaves out what
  // must not be marked (fields a destination doesn't carry, split-local fields,
  // non-member entities, destinations that never push), so read sites render it
  // as-is rather than filtering.
  //
  // OPTIONAL until the SC-3659 backend deploys; read sites must default to [].
  sync_states?: FieldSyncState[];
}

// Where one field stands at one destination (SC-3659). `held` is NOT a kind of
// waiting: the destination's policy won't send it, so it must never render as
// "waiting" — that would promise a sync that never comes. Nor is `no_external_key`
// (added 2026-10-22): the change would be sent, but the product has no key to
// address it by at the destination, so the run never pushes it. Per field — it
// covers exactly the fields that would otherwise read `waiting`.
export type FieldSyncStateKind = 'waiting' | 'held' | 'no_external_key' | 'failed' | 'rejected' | 'conflict';

export interface FieldSyncState {
  // Resolves through `GET /destinations` (name, enabled).
  destination_id: string;
  // The EKOM field name, as in `EntityResponse.data`.
  field: string;
  state: FieldSyncStateKind;
  // What this destination last accepted; null if it was never delivered.
  last_delivered_value: string | null;
}

export interface PaginatedResponse<T> {
  items: T[];
  page: number;
  limit: number;
  total: number;
}

// --- Staff support grants (SC-2668 BE / SC-2948 FE) --------------------------
// One row of `GET /admin/support-access` (EKOM_ADMIN-gated): a temporary,
// audited membership a staff user holds in a client org. This endpoint is the
// ONLY place the grant is visible — `/me` lists the same membership but without
// anything marking it as support. `membership_id` joins the two.
export interface SupportGrant {
  membership_id: string;
  user: User;
  organization: Organization;
  role: Role;
  status: MembershipStatus;
  granted_by: { id: string; name: string };
  reason: string;
  granted_at: string;
  // Request-time expiry is the access boundary: past this instant the grant is
  // dead on the next request even though the row may still be listed.
  expires_at: string;
  created_at: string;
}

// --- Team invitations (SC-2619 BE / SC-2795 FE) ------------------------------
// Wire values are lowercase (BE `InviteStatus` StrEnum). The tenant list read
// (`GET /team/invitations`) returns PENDING only, but the field is carried so a
// just-created / just-revoked invite renders its own state without a refetch.
export type InviteStatus = 'pending' | 'accepted' | 'revoked' | 'expired';

// The public invite shape — deliberately no org (implicit in the single-org
// request) and no token (the raw token only ever rides the create response).
export interface Invitation {
  id: string;
  email: string;
  role: Role;
  status: InviteStatus;
  expires_at: string;
  created_at: string;
}

// --- Team roster (SC-2882 BE / SC-3227 FE) -----------------------------------
// One ACTIVE member of the caller's org (`GET /team/members`, any active member
// may read it). `membership_id` addresses the title write; `user_id` is the
// cross-feature person handle. `email` is shown to every member of the same org.
export interface TeamMember {
  membership_id: string;
  user_id: string;
  name: string;
  email: string;
  title: string | null;
  role: Role;
}

// Deliberately unpaginated: the whole roster, sorted by name server-side.
export interface TeamMembersResponse {
  members: TeamMember[];
}

// The created invite plus its RAW single-use token. The token is surfaced here
// only while email delivery is a stub (SC-2622–2627) — hand-carried until then.
export interface CreateInvitationResponse {
  invitation: Invitation;
  token: string;
}

export type FindingStatus = 'pending' | 'acknowledged' | 'applied' | 'dismissed';

// SC-2291 canonical finding source. `profile_derived` = deterministic structural
// findings straight from the CatalogProfile (no LLM call — SC-2523 Gap #2: field
// splits + catalog reviews the freeform→structurer pipeline doesn't produce).
export type FindingSource = 'llm_freeform' | 'llm_curated' | 'profile_derived';

/**
 * Breadth a finding operates at (SC-2291). `value` = one entity's cell, `field` =
 * a whole column, `catalog` = the whole catalog. A finding's scope is the coarsest
 * granularity it touches. The Attributes surface reads `field`/`catalog`; `value`
 * findings live in the table overlay + entity drawer.
 */
export type Scope = 'value' | 'field' | 'catalog';

/**
 * The BE's action intent for a finding (SC-2291) — why it exists. Used only to
 * label a few apply CTAs on the FE; treat unknown values leniently.
 */
export type Intent =
  | 'normalize'
  | 'fill'
  | 'correct'
  | 'split'
  | 'reconcile'
  | 'deduplicate'
  | 'deprecate'
  | 'restructure'
  | 'provenance';

// ---------------------------------------------------------------------------
// ChangeSet (SC-2291) — the canonical resolution payload a finding carries.
// A finding is resolved EITHER by applying its `changeset` (a list of typed ops)
// OR by a `human_review` (a scoped question, no automatable write). Only the
// wire-serialized pydantic fields appear here; the BE's `verb`/`scope`/`describe`
// are computed @properties and are NOT on the wire (the FE re-derives display).
// ---------------------------------------------------------------------------

/** Per-entity cell write — fill / replace / remove one (entity, field) value. */
export interface ValueMutation {
  type: 'value';
  field: string;
  entity_id: string;
  before: string | null;
  after: string | null;
  // Ranked alternatives, best-first (candidates[0] === after) when the BE is torn
  // between grounded options; empty for a confident single value (the common case).
  candidates: string[];
}

/** Whole-column (or entity-subset) value transform — normalize case, trim, map, … */
export interface FieldTransform {
  type: 'transform';
  field: string;
  transform:
    | 'normalize_case'
    | 'trim'
    | 'map_values'
    | 'normalize_units'
    | 'normalize_date'
    | 'normalize_delimiter'
    | 'regex_replace'
    | 'set_constant';
  params: Record<string, unknown>;
  // Empty = apply to every entity value in the field (whole-column transform).
  entity_ids: string[];
}

/** Structural change to a column itself — rename / split / remove / add. */
export interface SchemaMutation {
  type: 'schema';
  op: 'rename' | 'split' | 'remove' | 'add';
  field: string;
  // rename → { to }, split → { split_into: string[] }; remove/add take none.
  params: Record<string, unknown>;
}

/** Look up / fill a value from an external source — whole field or an entity subset. */
export interface Enrichment {
  type: 'enrich';
  field: string;
  // Empty = enrich the whole field.
  entity_ids: string[];
  what: string;
  source_hint: string | null;
}

export type Operation = ValueMutation | FieldTransform | SchemaMutation | Enrichment;

export interface ChangeSet {
  ops: Operation[];
}

/**
 * A finding needing a person's judgment rather than an automatable fix — the
 * alternative to a `ChangeSet`. Carries no reversible write, so it never enters
 * `ChangeSet.ops` and lives on `Finding.human_review`.
 */
export interface HumanReview {
  scope: Scope;
  field: string | null;
  entity_ids: string[];
  question: string;
}

export interface LastAction {
  action: FindingStatus;
  at: string;
  by: string | null;
  // Captured at action-time (git-author semantics — survives later renames, SC-2157).
  by_user_name: string | null;
  snapshot: Record<string, unknown>;
  until: string | null;
}

export interface FindingResponse {
  id: string;
  catalog_id: string;
  organization_id: string;
  status: FindingStatus;
  source: FindingSource;
  intent: Intent;
  scope: Scope;
  // BE-rendered human-facing headline for the card (SC-2291). Always present.
  summary: string;
  // Fields the finding touches — the direct join to the Attributes rows.
  fields: string[];
  entity_ids: string[];
  // SC-3532 — entities the finding touches: resolved, deduped, falling back to the
  // citations for a finding never resolved. The count `sort=affected` orders by, so the
  // Affected column shows it. Optional: a BE predating it omits it.
  affected_count?: number;
  // Resolved EITHER by `changeset` OR `human_review`; the other is null.
  changeset: ChangeSet | null;
  human_review: HumanReview | null;
  // SC-3257 — true exactly when the whole-finding `POST …/apply` (no body) would
  // write; false when it would 409 (schema-only, unresolved enrich, a transform
  // that won't build, held ops, review-only). About the changeset, NOT status.
  auto_applicable: boolean;
  depends_on_fields: string[];
  severity: number;
  last_action: LastAction | null;
  confidence: number | null;
  rationale: string | null;
  // SC-3249 plain-language layers (SC-3352 renders them): "why it matters", and on a
  // decision-needed finding what we'd choose. Null on findings that predate them and
  // on every non-LLM finding.
  impact: string | null;
  recommendation: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedFindingsResponse {
  items: FindingResponse[];
  page: number;
  limit: number;
  /** Findings matching EVERY filter, across all pages (SC-3532). */
  total: number;
  /** The same statuses with no other filter — the "of N" beside a narrowed `total`. */
  unfiltered_total: number;
}

/** One option of the Findings field filter (SC-3533) — a field at least one matching
 *  finding touches, with how many. */
export interface FindingFieldCount {
  field: string;
  count: number;
}

export interface FindingFieldsResponse {
  items: FindingFieldCount[];
}

// SC-2580 — async whole-finding apply. A large whole-finding apply moves off the
// request thread: POST …/apply returns `202` + a job (instead of the synchronous
// `204`), and the UI polls GET …/apply until the job reaches a terminal status.
// The single-entity per-row Fix (`entity_id` in the body) stays synchronous.
export type ApplyJobStatus = 'queued' | 'applying' | 'applied' | 'failed';

/** `200` body from `POST …/apply` with an `entity_id` — the per-entity sync Fix
 *  (SC-2597). Confirms resolution from the write itself, no poll round-trip:
 *  the targeted entity is resolved iff absent from `open_entity_ids`;
 *  `fully_applied` means the WHOLE finding is done. `applied`/`skipped` count
 *  the cells THIS fix touched. Shares the ledger shape with {@link ApplyProgress}. */
export interface ApplyOutcome {
  finding_id: string;
  applied: number;
  skipped: number;
  fully_applied: boolean;
  open_entity_ids: string[];
}

/** `202` body from `POST …/apply` when applying a whole finding (no `entity_id`). */
export interface ApplyJobResponse {
  job_id: string;
  finding_id: string;
  status: ApplyJobStatus;
}

/**
 * `GET …/apply` — the poll projection. `status: null` = no apply job has ever
 * existed for this finding.
 *
 * ⚠️ Counts are ValueMutation-cell-based: a finding with no value cells (a
 * `human_review`, or a whole-field structural `SchemaMutation`) reports
 * `total_cells: 0` / `fully_applied: false` *even once it completes*. Use
 * `status` — never `fully_applied` — as the completion signal, and render an
 * indeterminate "applying…" state (not a `0/0` bar) when `total_cells === 0`.
 */
export interface ApplyProgress {
  finding_id: string;
  status: ApplyJobStatus | null;
  total_cells: number;
  applied_cells: number;
  open_entity_ids: string[];
  fully_applied: boolean;
  error: string | null;
}

// History feed (SC-3424) — `GET /normalization/catalogs/{id}/history`. One newest-first
// feed over the change ledger (every write: finding applies whole or per-row, cell
// edits, field splits + discards, source refreshes, undos) and the two finding
// judgments that write no data (acknowledged / dismissed).
export type HistoryAction =
  | 'applied'
  | 'edited'
  | 'split'
  | 'split_discarded'
  | 'source_refresh'
  | 'undone'
  | 'acknowledged'
  | 'dismissed';

export interface HistoryActor {
  name: string;
  /** `system` = an EKOM subsystem (name like "EKOM source refresh"). */
  kind: 'human' | 'system';
  id: string | null;
}

/** One sampled cell of a change, as written. Values arrive whole (SC-3517). */
export interface HistoryCell {
  /** The row's Mongo `_id` — what "Open product" loads by. Null on a legacy
   *  (pre-SC-3299) cell, which recorded no row: render it without a link. */
  entity_id: string | null;
  /** The row's display label at write time. Shown as-is, never looked up. */
  label: string | null;
  field: string;
  before: string | null;
  after: string | null;
}

export interface HistoryEventResponse {
  id: string;
  /** `ledger` = a data write (one gesture); `finding` = a judgment on a finding. */
  source: 'ledger' | 'finding';
  action: HistoryAction;
  at: string;
  actor: HistoryActor;
  /** Set on a person's cell edit: typed in (`ui`) or approved from the agent. */
  via: 'ui' | 'agent' | null;
  /** BE-authored one-liner. */
  summary: string;
  fields: string[];
  /** Total cells the change wrote; null on judgments (they write none). */
  cell_count: number | null;
  /** Up to 3 cells with before → after; empty for splits and judgments. */
  cell_sample: HistoryCell[];
  finding_id: string | null;
  gesture_id: string | null;
  reverses_gesture_id: string | null;
  snoozed_until: string | null;
}

export type PaginatedHistoryResponse = PaginatedResponse<HistoryEventResponse>;

export type Confidence = 'low' | 'medium' | 'high';

export interface FieldHeuristic {
  name: string;
  cardinality: number;
  blank_rate: number;
  top_values: Record<string, number>;
  looks_compound: boolean;
}

export interface FieldProfile {
  name: string;
  is_identity: boolean;
  description: string;
  dependent_fields: string[];
  is_atomic: boolean;
  suggested_splits: string[];
  is_categorical: boolean;
  cardinality: number;
  blank_rate: number;
  top_values_sample: string[];
}

export interface VerticalSpecificField {
  name: string;
  description: string;
  rationale: string;
  // null → "appears missing" (actionable); non-null → "already present as <name>"
  likely_present_as: string | null;
}

export interface FieldProfileOverride {
  is_identity: boolean | null;
  description: string | null;
  dependent_fields: string[] | null;
  is_atomic: boolean | null;
  suggested_splits: string[] | null;
  is_categorical: boolean | null;
}

export interface CatalogProfileOverride {
  vertical: Vertical | null;
  categorization_fields: string[] | null;
  // null → defer to the LLM's choice; [] → explicit "no preview fields";
  // [...] → user override (BE syncs on `is not None`, so [] is honored).
  preview_fields: string[] | null;
  // Keyed by field name
  fields: Record<string, FieldProfileOverride> | null;
  // Note: the BE PATCH body accepts `locked_fields` as a no-op (captured but
  // not propagated pending future lock-semantics consumers — SC-2233 brief).
  // Omitted here because the FE has no consumer; add it back when lock
  // semantics ship.
}

export interface CatalogProfileResponse {
  id: string;
  catalog_id: string;
  vertical: Vertical;
  vertical_confidence: Confidence;
  vertical_reasoning: string;
  fields: FieldProfile[];
  categorization_fields: string[];
  vertical_specific_fields: VerticalSpecificField[];
  // LLM-curated 5-10 informative fields for diff-card / detail-preview rendering.
  // Order matters — most informative first. BE-enforced max of 10.
  preview_fields: string[];
  overall_reasoning: string;
  // User-edit overlay. Slice A renders the base profile and ignores this; will
  // become live when the override-editing follow-up ticket lands.
  user_overrides: CatalogProfileOverride | null;
  // Per-field heuristic measurements, keyed by field name (matches fields[i].name).
  heuristics: Record<string, FieldHeuristic>;
  last_regenerated_at: string | null;
  regeneration_count: number;
  created_at: string;
  updated_at: string;
}

// Attributes view (SC-2381) — the schema-axis counterpart to the entity-data
// table: per-field "what we have & how we interpreted it". The FE consumes it
// for the SC-2392 drawer field grouping (and, later, the column manager + facet
// rail). Served by `GET …/normalization/catalogs/{id}/attributes`; 404 until the
// catalog profile exists (same surface as `/profile`). As-built per the SC-2381
// BE handoff (PR #78) — values are RAW (not override-merged); badge edits via
// `is_overridden`.

// How the BE interpreted a field — drives the facet widget (Slice E) and is the
// source of `is_identity`. Lowercase per the as-built StrEnum.
export type FieldRole = 'identity' | 'categorical' | 'measure' | 'text' | 'temporal' | 'media';

// A reconciled drawer section: a group label + its member field names in display
// order. `field_names` join on `AttributeView.field` (NOT a duplicated object).
// `groups` partitions ALL fields with a trailing `OTHER` catch-all, so the drawer
// is a pure `groups.map(...)` — no leftover-handling required.
export interface FieldGroup {
  label: string;
  field_names: string[];
}

// One field's full schema-axis projection. The 12 shared keys mirror the BE's
// `FieldView` (also returned by the `describe_field` agent tool, so they can't
// drift); `is_locked`/`is_overridden` are the catalog-level override flags.
export interface AttributeView {
  field: string;
  description: string;
  role: FieldRole;
  // The drawer SECTION (IDENTITY / ELECTRICAL / …); normalized, never blank.
  group: string;
  is_identity: boolean;
  is_atomic: boolean;
  // Non-empty only when `is_atomic` is false.
  suggested_splits: string[];
  dependent_fields: string[];
  cardinality: number;
  // Fraction missing (0.0–1.0); `fill_rate` is the derived `round(1 - blank_rate, 4)`.
  blank_rate: number;
  fill_rate: number;
  sample_values: string[];
  // In `user_overrides.locked_fields`.
  is_locked: boolean;
  // Has an entry in `user_overrides.fields`.
  is_overridden: boolean;
}

export interface AttributesResponse {
  // CANONICAL (source) order — the column-manager default + virtualization index;
  // independent of `groups` (section) order.
  fields: AttributeView[];
  groups: FieldGroup[];
}

export type AnalysisState = 'not_started' | 'running' | 'complete' | 'failed';
export type AnalysisPhase = 'catalog_profile' | 'analysis';

/**
 * Status of the upload analysis pipeline for a single catalog. Drives the
 * global AnalysisStatusBar + inline AnalysisInProgressBanner.
 *
 * Wire shape mirrors the backend's AnalysisStatusResponse (SC-1871 BE follow-up).
 *  - `state`: not_started → running → complete (or failed)
 *  - `current_phase`: populated while state is `running`; null otherwise
 *  - `failed_phase` + `error`: populated only when state is `failed`
 */
export interface AnalysisStatusResponse {
  state: AnalysisState;
  current_phase: AnalysisPhase | null;
  started_at: string | null;
  completed_at: string | null;
  failed_phase: AnalysisPhase | null;
  error: string | null;
}

export interface AuthResponse {
  user: User;
  organization: Organization | null;
  memberships: Membership[];
  access_token: string;
  refresh_token: string;
}

export interface MeResponse {
  user: User;
  organization: Organization | null;
  memberships: Membership[];
}

// Agent chat content blocks. Discriminated union by `type`. Wire contract:
// SC-1612 frontend-brief.md. `thinking` and `redacted_thinking` are persisted
// for Anthropic protocol compliance but never rendered.
export interface ChatTextBlock {
  type: 'text';
  text: string;
}

export interface ChatToolUseBlock {
  type: 'tool_use';
  id: string;
  name: string;
  input: unknown;
}

export interface ChatToolResultBlock {
  type: 'tool_result';
  tool_use_id: string;
  // User-facing label for the tool (e.g. "Field values"). Pair with the raw
  // tool name from the matching ToolUseBlock — BE owns this mapping and may
  // grow it as Part B adds tools.
  display_name: string;
  summary: string;
  is_error: boolean;
  // Structured tool output. Write tools using the confirmation round-trip
  // (SC-2157) carry `{ needs_confirmation, proposed_action, summary }` here on
  // the first invocation. Read tools carry their `model_dump()` payload.
  // Parity between live SSE (`tool_use_done`) and persisted history.
  content?: Record<string, unknown> | string;
}

// Diff-card payloads embedded inside agent tool_result `content` (SC-2231 /
// SC-2043). The diff data lives INSIDE each tool's content — there is no
// top-level `references` field. The REST /findings payload is a separate,
// unchanged shape (FindingPayload above); these types describe only what the
// three diff-bearing agent tools emit.

// An entity projected for preview. `data` is PROJECTED — only a subset of
// fields (the identifier field + the diff's field + the catalog's preview
// fields, or whatever the agent requested). Do NOT assume all attributes are
// present, and values are `unknown` — coerce before display.
export interface EntityPreview {
  // The KEY: unique, and what every resolving path matches on — get_entities,
  // apply_finding, the identifier filter, drill-in. Null only for pre-SC-2191
  // / unidentified rows.
  identifier: string | null;
  // What a PERSON calls the row, derived from the catalog's display key
  // (SC-3032). NOT unique — never use it as a lookup value, a React key or a
  // drill-in target; those all stay on `identifier`. Null when the entity has
  // no usable display value, and for any catalog whose profile pass hasn't
  // picked a display key yet — callers fall back to `identifier`.
  label: string | null;
  data: Record<string, unknown>;
}

// A value op's verb (fill/replace/remove), a human review, or — for an op with no
// single proposed value — the op kind (enrich/transform/schema), which carries its
// BECOMES on `descriptor` instead (SC-2459 BE, rendered since SC-3444).
export type DiffActionVerb =
  | 'replace'
  | 'fill'
  | 'remove'
  | 'flag_for_review'
  | 'enrich'
  | 'transform'
  | 'schema';

// The before/after card payload. Present only for entity-scoped findings
// (entity_action / bulk_entity_action). There is no `current_value` — derive
// the "before" as `rep_entity.data[field]` (may be undefined for a fill on an
// empty field; render blank).
export interface FindingDiff {
  field: string;
  proposed_value: string | null; // null for flag_for_review / enrich / transform / schema
  action_verb: DiffActionVerb;
  // The BECOMES line for an op with no single proposed value ("Normalize
  // capitalization", "Rename → Size"); null/absent for value ops and reviews.
  descriptor?: string | null;
  rep_entity: EntityPreview;
  // Distinct current values when 2+ entities normalize to ONE value on this field
  // (['black','BLACK','Black'] → 'black'); null otherwise. (The FE read this as
  // `bulk_variants`, a name the wire never carried, until SC-3444.)
  current_value_variants: string[] | null;
}

// Discriminator payload for the confirmation round-trip — present on a
// tool_result's `content` when the agent is asking the user to approve a
// pending write. SC-2157 wire contract; `diff` added in SC-2231.
export interface ConfirmationRequestContent {
  needs_confirmation: true;
  proposed_action: {
    finding_id: string;
    // Present when the agent scoped the apply to specific rows (SC-2230): the
    // targeted business identifiers (SKUs). Absent → whole-finding apply.
    entity_ids?: string[];
    // Total entities the apply touches (SC-2750): len(entity_ids) when scoped,
    // else the finding's affected-entity count. Absent on pre-SC-2750 BE — the
    // FE then falls back to a number-free scope label (see applyScopeLabel).
    affected_count?: number;
  } & Record<string, unknown>;
  summary: string;
  diff?: FindingDiff | null; // render before/after on the approval card
}

// Parameter typed as `unknown` so the predicate doesn't have to prove that
// ConfirmationRequestContent is structurally assignable to ChatToolResultBlock's
// `content` field — its `needs_confirmation: true` literal doesn't fit
// Record<string, unknown>. Callers pass tool_result.content which is always
// assignable to unknown.
export function isConfirmationRequest(
  content: unknown,
): content is ConfirmationRequestContent {
  return (
    typeof content === 'object' &&
    content !== null &&
    (content as { needs_confirmation?: unknown }).needs_confirmation === true
  );
}

// Legacy finding-kind discriminator — retained ONLY for the agent-chat
// `get_findings` tool contract ({@link ChatFindingSummary}), which the SC-2291
// ChangeSet migration hasn't reached yet (that's the agent-surface ticket,
// SC-2230). The `/findings` list path no longer carries a `kind` — findings there
// are ChangeSet/`human_review`-shaped (see {@link FindingResponse}).
export type FindingKind =
  | 'pattern'
  | 'gap'
  | 'entity_action'
  | 'bulk_entity_action'
  | 'field_action'
  | 'catalog_action';

// One finding as returned inside `get_findings` content. `diff` is non-null
// only for entity_action / bulk_entity_action (and null when the rep entity
// was deleted); render a DiffCard when present, otherwise render as today.
export interface ChatFindingSummary {
  id: string;
  severity: number; // inverted: lower = higher priority
  kind: FindingKind;
  status: FindingStatus;
  summary: string;
  field: string | null;
  item_ids: string[] | null;
  // The LLM's reasoning for why the finding was raised — surfaced per change so
  // the user understands the proposal. Null for heuristic detections.
  rationale: string | null;
  diff: FindingDiff | null;
}

// `content` shape for the `get_findings` tool.
export interface GetFindingsContent {
  findings: ChatFindingSummary[];
  total_matching: number;
  returned: number;
}

// `content` shape for the `get_entities` tool. SC-2043 reshaped `entities`
// from bare data dicts to EntityPreview.
export interface GetEntitiesContent {
  entities: EntityPreview[];
  total_entities: number;
}

// `content` shape for `apply_finding` after approval (confirmed=true) — the
// SYNCHRONOUS "applied" result. The confirmed=false branch is
// ConfirmationRequestContent above; the async "started" branch is
// StartedApplyingOutputContent below. Both results carry `needs_confirmation: false`,
// so `outcome` is the discriminator (BE 1b-ii-b). `rationale` = the LLM's reasoning,
// surfaced on the applied card; null when the finding carries none.
export interface ApplyFindingOutputContent {
  needs_confirmation: false;
  outcome: 'applied';
  finding_id: string;
  applied_at: string; // ISO datetime
  diff: FindingDiff | null; // pre-apply snapshot
  rationale: string | null;
  /** SC-3476 — what the one-entity apply actually did: a cell written, nothing left
   *  to change, the cited value names no single row, or the cell changed since the
   *  finding was raised (the last two write nothing). Absent on chat rows persisted
   *  before the BE sent it. */
  apply_result?: 'applied' | 'already_applied' | 'no_match' | 'drifted';
}

// Tool-keyed guards. Callers pass the raw tool name (AssistantToolItem.name)
// so we trust the BE's tool dispatch rather than sniffing structure alone; the
// structural check guards against an unparsed string `content`.
export function isGetFindingsContent(
  name: string,
  content: unknown,
): content is GetFindingsContent {
  return (
    name === 'get_findings' &&
    typeof content === 'object' &&
    content !== null &&
    Array.isArray((content as { findings?: unknown }).findings)
  );
}

export function isGetEntitiesContent(
  name: string,
  content: unknown,
): content is GetEntitiesContent {
  return (
    name === 'get_entities' &&
    typeof content === 'object' &&
    content !== null &&
    Array.isArray((content as { entities?: unknown }).entities)
  );
}

export function isApplyFindingOutput(
  content: unknown,
): content is ApplyFindingOutputContent {
  if (typeof content !== 'object' || content === null) return false;
  const c = content as { needs_confirmation?: unknown; outcome?: unknown };
  // Both result variants carry needs_confirmation:false; `outcome` discriminates
  // them (BE 1b-ii-b). Match the synchronous "applied" card and exclude the async
  // "started" card. Legacy replay rows predate `outcome` (absent) → still applied.
  return c.needs_confirmation === false && c.outcome !== 'started';
}

// SC-2580 — `content` shape for an apply_finding tool call that ENQUEUED an async
// whole-finding apply (BE 1b-ii-b `ApplyStartedOutput`): the agent returns
// immediately (no in-turn completion card), the finding's summary + entity count
// live in the agent's TEXT message, and the card carries the pre-apply `diff`. The
// user tracks progress on the table/drawer poll (keyed on `finding_id`).
// Discriminated from the synchronous "applied" card by `outcome` (both share
// `needs_confirmation: false`), so isStartedApplyingOutput is checked BEFORE
// isApplyFindingOutput.
export interface StartedApplyingOutputContent {
  needs_confirmation: false;
  outcome: 'started';
  finding_id: string;
  /** The enqueued job's id — echoed for correlation; the FE polls by finding_id. */
  job_id: string;
  /** Pre-apply snapshot (representative entity); null for whole-field/schema ops. */
  diff: FindingDiff | null;
  /** The LLM's reasoning, when the finding carries it. */
  rationale: string | null;
}

export function isStartedApplyingOutput(
  content: unknown,
): content is StartedApplyingOutputContent {
  if (typeof content !== 'object' || content === null) return false;
  const c = content as { needs_confirmation?: unknown; outcome?: unknown };
  return c.needs_confirmation === false && c.outcome === 'started';
}

export interface ChatThinkingBlock {
  type: 'thinking';
  thinking: string;
  signature: string;
}

export interface ChatRedactedThinkingBlock {
  type: 'redacted_thinking';
  data: string;
}

// SC-2853 (BE SC-2753) — the frozen model-facing render of the canvas snapshot
// that rode a user message, replayed at the head of that turn's content so the
// model re-reads the same bytes every turn. NEVER rendered in the user's bubble
// (groupTurns keeps user bubbles to `text` blocks — and skips unknown block
// types generally, so future BE block types don't break replay either).
export interface ChatCanvasContextBlock {
  type: 'canvas_context';
  text: string;
}

export type ChatContentBlock =
  | ChatTextBlock
  | ChatToolUseBlock
  | ChatToolResultBlock
  | ChatThinkingBlock
  | ChatRedactedThinkingBlock
  | ChatCanvasContextBlock;

export type ChatRole = 'user' | 'assistant';

export interface ChatUsage {
  input_tokens: number;
  output_tokens: number;
  cache_creation_input_tokens: number;
  cache_read_input_tokens: number;
}

export interface ChatTurnResponse {
  id: string;
  role: ChatRole;
  content: ChatContentBlock[];
  created_at: string;
  usage: ChatUsage | null;
}

export interface ChatSessionResponse {
  id: string;
  created_at: string;
  updated_at: string;
  turns: ChatTurnResponse[];
}

// Briefing (SC-2242) — narrative + recommended_action + catalog/user meta.
// Backend endpoint: GET /catalogs/{id}/briefing?refresh=false. Replaces the
// old `DashboardResponse` composite; bucket counts now live on a separate
// `/finding-counts` endpoint (see FindingCountsResponse below).
export interface BriefingResponse {
  catalog: {
    id: string;
    name: string;
    entity_count: number;
    attribute_count: number;
    last_synced_at: string;
    // Per-catalog entity terminology (SC-2191). See CatalogResponse for the
    // defaults rationale.
    entity_term_singular: string;
    entity_term_plural: string;
  };
  user: {
    // Empty string is possible per the BE brief — the FE should fall back to
    // "there" for the greeting in that case.
    preferred_name: string;
  };
  narrative: {
    summary: string;
    generated_at: string;
    source: NarrativeSource;
  };
  // BE revalidates `finding_id` on every GET (SC-2242). If the referenced
  // finding has been APPLIED / DISMISSED / deleted, `recommended_action`
  // serializes as null and the FE renders starter chips instead of the CTA.
  recommended_action: {
    finding_id: string;
    reason: string;
    // Pre-composed first-turn message in the user's voice ("Let's start with…").
    // BE returns this directly; FE POSTs it verbatim as the chat seed.
    seed_message: string;
  } | null;
}

// `template` = empty catalog / all-caught-up; `llm` = LLM-generated narrative;
// `template_fallback` = LLM call failed and BE fell back to the templated path;
// `template_pipeline_pending` (SC-2242) = ephemeral analyzing-briefing rendered
// while the structurer phase is still running on a fresh upload. The FE
// branches on this value to render an in-bubble "analyzing" treatment.
export type NarrativeSource =
  | 'template'
  | 'llm'
  | 'template_fallback'
  | 'template_pipeline_pending';

// Live bucket counts (SC-2242). Backend endpoint:
// GET /normalization/catalogs/{id}/finding-counts. Pure live DB read, no
// caching. The FE coordinates suppression during pipeline runs via
// useAnalysisStatus (counts return 0/0/0 legitimately while a structurer
// run is in flight).
export interface FindingCountsResponse {
  done: BucketCounts;
  ready: BucketCounts;
  your_call: BucketCounts;
}

export interface BucketCounts {
  finding_count: number;
  item_count: number;
}

// ── Catalog Info / onboarding context (SC-2445) ──────────────────────────────
// Structured, per-catalog context a user provides about their catalog, surfaced
// into LLM prompts. Three pillars: (A) brand & content voice — feeds enrichment /
// PDP + SEO copy; (B) catalog standards & normalization intent — feeds the
// analysis/findings pass; (C) a free concerns catch-all → the freeform
// `client_context` block. Brand fields (A) are named to match the BE's planned
// EnrichmentProfile Layer 2 so they can be promoted org-level later without a
// redesign. Persisted via GET/PATCH /catalogs/{id}/context (BE half: SC-2448).

// A clarifying question the profile pass proposes (BE layer 2, SC-2446). The FE
// renders it as a typed control in the question queue today; the list is empty
// until that BE work lands. Each question is grounded in the catalog's own
// fields and maps to the CatalogContext key its answer populates (confirm, not
// compose).
export type ClarifyingQuestionKind = 'boolean' | 'single_select' | 'pick_field' | 'short_text';

export interface ClarifyingQuestion {
  id: string;
  kind: ClarifyingQuestionKind;
  // The question, grounded in the catalog's own fields/values.
  prompt: string;
  // Catalog field(s) the question is about — e.g. ['feature_1b_size'].
  referenced_fields: string[];
  // Which CatalogContext key the answer writes back to (a key of CatalogContext).
  target_context_key: string;
  // For `single_select`: the choices. (`pick_field` choices are the catalog fields.)
  options?: string[];
  // The profile's best-guess answer — rendered as a confirm-not-compose default.
  suggested_answer?: string | boolean | null;
  // The user's answer; null until answered.
  answer?: string | boolean | null;
}

// House glossary the model can't infer: preferred terms vs. avoid/banned ones.
export interface TerminologyContext {
  preferred: string[];
  avoid: string[];
}

export interface CatalogContext {
  catalog_id: string;
  // Pillar A — brand & content voice.
  brand_voice: string;
  target_audience: string;
  terminology: TerminologyContext;
  // Pillar B — catalog standards & normalization intent.
  taxonomy_intent: string;
  attribute_requirements: string;
  normalization_rules: string;
  publish_targets: string[];
  known_issues: string;
  // Pillar C — free concerns → the freeform `client_context` seam.
  additional_notes: string;
  // Layer-2 question queue (SC-2446); FE renders an empty list as a quiet hint.
  clarifying_questions?: ClarifyingQuestion[];
  updated_at?: string | null;
}

// PATCH body — partial upsert. `catalog_id`/`updated_at` are server-owned.
export type CatalogContextInput = Partial<Omit<CatalogContext, 'catalog_id' | 'updated_at'>>;

// --- Sync destinations / channels (SC-3104 BE, SC-3146 FE) -------------------
// A "destination" is where enriched data goes: the PIM, Shopify, Amazon, Syndigo.
// The FE calls them CHANNELS in user-facing copy — "destination" is the backend's
// word and shouldn't reach a catalog owner (the no-internal-jargon rule).
//
// `GET /destinations` is UNGATED (any active member) and deliberately narrow: no
// field-scoping config, no catalog scope. It exists so the FE can turn a row's
// `destination_ids` into a human label — a member who can see a row must be able
// to name the channel it belongs to, or chips render as raw ObjectIds.

// Literal unions — BE drift surfaces at compile time (the house convention). A
// SYSTEM_OF_RECORD destination owns values; a CHANNEL is a projection that never
// feeds baseline. Both can carry membership.
export type DestinationKind = 'system_of_record' | 'channel';

// Whether membership is consulted at all. `all_entities` means every row of an
// in-scope catalog reaches it WITHOUT carrying its id — so a row's
// `destination_ids` is NOT the whole truth about where that row goes, and the
// `destination` table filter deliberately means "reaches", not "is tagged with".
export type EntityScope = 'all_entities' | 'members_only';

export interface Destination {
  id: string;
  // The stable handle. Everything that WRITES speaks slugs — the table filter,
  // both membership endpoints and the upload param — while the entity read hands
  // back `destination_ids`. The FE owns that join.
  slug: string;
  name: string;
  kind: DestinationKind;
  entity_scope: EntityScope;
  // An adapter KEY, never a credential. Tells us whether a destination is a live
  // integration or a membership-only export target (Amazon and Syndigo exist as
  // export scopes long before anything pushes to them).
  provider: string;
  // Disabled destinations ARE returned, on purpose: an entity can belong to a
  // paused channel. Render them as paused; never filter them out of the list, or
  // that membership renders as an unresolvable id.
  enabled: boolean;
  // SC-3359 — whether the destination can actually carry data right now, derived
  // server-side from `enabled` AND lifecycle, integration and credentials. NOT the
  // same as `enabled`: a destination being deleted, or one with no credentials,
  // is enabled yet `not_connected` / `no_credentials`. Read this, not `enabled`,
  // to say whether a change will reach it.
  connection_status: DestinationConnectionStatus;
  created_at: string;
  updated_at: string;
}

// SC-3359 — the backend's own ordering decides it (deleting > planned > missing
// credentials > switched off > connected); the FE renders it, never recombines
// `enabled` with anything. A value this build doesn't know means "can't carry
// data" — read sites treat anything but `connected` that way.
export type DestinationConnectionStatus =
  | 'connected'
  | 'not_connected'
  | 'planned'
  | 'no_credentials'
  | 'credentials_invalid';

// Whether a destination is live or being torn down (SC-3121). Separate from
// `enabled` because only one of them is reversible: `deleting` is ONE-WAY — there is
// no route back to `active`, so the UI must never offer a cancel or a toggle on it.
export type DestinationLifecycle = 'active' | 'deleting';

/**
 * One destination in FULL — the `MANAGE_DESTINATIONS`-gated `GET/PATCH
 * /destinations/{id}` shape (SC-3121). Adds the boundary policy the ungated list
 * omits.
 */
export interface DestinationDetail extends Destination {
  lifecycle: DestinationLifecycle;
  // ⚠️ `null` = EVERY catalog the org has, now and in future; `[]` = none. The two
  // must never render alike — collapsing them is the destructive-default mistake.
  catalog_ids: string[] | null;
  // Provider settings — documented secret-free by the backend, but not rendered.
  config: Record<string, unknown>;
  // The NAME of the env var holding the credential, never the secret itself.
  credentials_ref: string | null;
  // `null` = every field.
  allowed_fields: string[] | null;
  denied_fields: string[];
  field_map: Record<string, string>;
  // The analysis `Confidence` IntEnum (80/50/…), NOT the profile's string union —
  // hence `number[]`. Readable, not patchable.
  auto_cross_bands: number[];
  auto_cross_human: boolean;
}

// --- Inbound sources (app/sources; SC-3122 is the first FE reader) ----------
// `GET /sources` is gated by `view_sources` (ORG_ADMIN) and read-only — mutations
// are script-only (`make manage-sources`) until a settings UI grows a write gate.

// What KIND of source — seeds the default trust weight; the weight is the number.
export type SourceCategory = 'client_stack' | 'authoritative' | 'inferred';

// The adapter family (fetch mechanism). Not an access-legitimacy axis.
export type AdapterKind = 'connector' | 'scraper' | 'builtin';

export interface DataSource {
  id: string;
  name: string;
  slug: string;
  category: SourceCategory;
  // Trust weight, 0–1.
  weight: number;
  // The connection key — the same key a destination's `provider` names when one
  // integration is both a source and a destination.
  adapter_key: string;
  // `null` = the recorded `adapter_key` has no registered adapter in this build (a
  // stale record). Surfaced, not hidden.
  adapter_kind: AdapterKind | null;
  config: Record<string, unknown>;
  // Env var NAME, never a secret.
  credentials_ref: string | null;
  enabled: boolean;
  origin_group: string | null;
  // Same convention as a destination's `catalog_ids`: `null` = all, `[]` = none.
  catalogs: string[] | null;
  created_at: string;
  updated_at: string;
}

// --- Channel membership writes (SC-3104 BE, SC-3147 FE) ----------------------

/**
 * Which entities a membership gesture applies to.
 *
 * A UNION rather than one object with two optional fields, because the backend
 * refuses the degenerate shapes rather than resolving them: `{}` and
 * `{filters: []}` are a 422 ("no selection given"), and passing `filters` AND
 * `apply_to_all` together is a 422 too ("refusing to guess which selection was
 * meant"). Modelling it this way makes both refusals unrepresentable in our
 * types instead of discoverable at runtime — the `?: never` arms are what stop
 * an object literal carrying both.
 *
 * That strictness is deliberate on their side and worth preserving on ours: an
 * empty selection is one flag away from every row in a 120,000-item catalog, and
 * the remove direction would silently empty a channel's whole assortment.
 *
 * Build it with `membershipSelection()` rather than by hand — the one shape the
 * type still permits and the wire rejects is a non-empty `filters` list that
 * nonetheless compiles to match-all.
 */
export type MembershipSelection =
  | { filters: TableFilter[]; apply_to_all?: never }
  | { apply_to_all: true; filters?: never };

/** What a membership gesture touched. */
export interface MembershipChange {
  /**
   * Entities the selection matched, whether or not their membership needed
   * changing. Zero means the selection found no rows — almost always a user
   * mistake, and the reason this is reported separately from `changed`.
   */
  matched: number;
  /**
   * Entities whose membership actually changed. Zero alongside a positive
   * `matched` is a legitimate idempotent no-op ("already in Shopify"), NOT an
   * error — the add is an `$addToSet` server-side.
   */
  changed: number;
}
